import { Box, Breadcrumbs, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Loading, useDataProvider, useNotify, useTranslate } from 'react-admin';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { apiUrl, httpClient } from '../../utils/httpUtils';
import { Actions } from './Actions';
import { Step1 } from './Step1';
import { Step2 } from './Step2';
import { Success } from './Success';
import { validations } from './validations';

const CustomForm = ({ step, setStep, record, data }) => {
  const navigate = useNavigate();
  const [teams, setTeams] = useState();
  const notify = useNotify();
  const {
    setValue,
    control,
    register,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...record,
    },
  });

  useEffect(() => {
    if (record && data) {
      let all_teams = data?.map(i => ({ team_id: i?.id, name: i?.name }));
      const record_teams = record?.teams_goals;
      const record_ids = record_teams.map(item => item.team_id);
      all_teams = all_teams.filter(i => !record_ids.includes(i.team_id));
      setTeams([...record_teams, ...all_teams]);
    }
  }, [record, data]);

  const saveItems = async data => {
    const response = await httpClient(apiUrl + '/goals/', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (response.status >= 200 && response.status < 300) {
      return true;
    }
    notify(response.message || 'ra.notification.http_error', { type: 'error' });
    return false;
  };

  function removeNumericFormat(formattedNumber) {
    if (!formattedNumber) return '';
    if (typeof formattedNumber !== 'string') return formattedNumber;
    let rawNumber = formattedNumber.replace(/(\.|,)/g, '');
    rawNumber = rawNumber.replace(/(\.|,)(?=\d{2}$)/, '.');

    return Number(rawNumber || 0);
  }

  const onSubmit = async values => {
    const teams = values.teams_goals?.map(i => {
      const team_goal = {
        team_id: i?.team_id,
        percentage_visits: Number(i?.percentage_visits || 0),
        percentage_notes: Number(i?.percentage_notes || 0),
        percentage_rfv: Number(i?.percentage_rfv > 0 ? values?.rfv : 0),
        percentage_subscribers: Number(i?.percentage_subscribers || 0),
        point_visits: Number(i?.point_visits || 0),
        point_notes: Number(i?.point_notes || 0),
        point_rfv: Number(i?.point_rfv || 0),
        point_subscribers: Number(i?.point_subscribers || 0),
      };
      if (i?.id) {
        team_goal.id = i.id;
      }
      return team_goal;
    });
    const body = {
      notes: removeNumericFormat(values?.notes),
      visits: removeNumericFormat(values?.visits),
      rfv: removeNumericFormat(values?.rfv),
      subscribers: removeNumericFormat(values?.subscribers),
      month: values?.month,
      teams_goals: teams,
    };

    const res = await saveItems(body);
    if (!res) {
      return false;
    }
    return true;
  };

  const handleReset = () => {
    //go to create
    navigate('/goals/create');
  };

  useEffect(() => {
    if (Array.isArray(teams) && teams?.length > 0) {
      let new_teams = teams?.map(i => ({
        id: i?.id,
        team_id: i?.team_id,
        name: i?.name,
        percentage_visits: i?.percentage_visits || undefined,
        percentage_notes: i?.percentage_notes || undefined,
        percentage_rfv: i?.percentage_rfv || undefined,
        percentage_subscribers: i?.percentage_visits || undefined,
        point_visits: i?.point_visits || undefined,
        point_notes: i?.point_notes || undefined,
        point_rfv: i?.point_rfv || undefined,
        point_subscribers: i?.point_subscribers || undefined,
      }));
      setValue('teams_goals', new_teams, {
        shouldDirty: true,
        shouldTouch: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teams]);

  if (!teams)
    return <Loading loadingSecondary="resources.goals.create.loading" />;

  return (
    <form>
      <Step1
        setTeams={setTeams}
        step={step}
        control={control}
        register={register}
        watch={watch}
        errors={errors}
      />
      <Step2
        step={step}
        control={control}
        register={register}
        watch={watch}
        errors={errors}
      />
      <Actions
        step={step}
        setStep={setStep}
        lastStep={2}
        handleLastStep={onSubmit}
        validations={validations}
        register={register}
        watch={watch}
        errors={errors}
        setError={setError}
        clearErrors={clearErrors}
      />
      {step === 3 ? <Success handleClick={handleReset} mode={'edit'} /> : null}
    </form>
  );
};

export const GoalsEdit = () => {
  let location = useLocation();
  const record = location.state;
  const [step, setStep] = useState(1);
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const [data, setData] = useState();

  const getTeams = async () => {
    let { data } = await dataProvider.getList('teams', {
      pagination: { page: 1, perPage: 100000 },
      sort: { field: 'name', order: 'ASC' },
      filter: {},
    });
    setData(data);
  };

  useEffect(() => {
    getTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!record && !data) return null;
  return (
    <Box pt={2}>
      <Breadcrumbs aria-label="breadcrumb">
        <Typography
          color="inherit"
          sx={{ fontSize: '14px', fontWeight: '400', lineHeight: '19px' }}
          component={Link}
          to="/goals"
        >
          {translate('resources.goals.name')}
        </Typography>
        <Typography
          color="textPrimary"
          sx={{ fontSize: '14px', fontWeight: '600', lineHeight: '19px' }}
        >
          {translate('resources.goals.edit.title')}
        </Typography>
      </Breadcrumbs>
      <Box maxWidth={{ xs: '100%', sm: 850 }} mt={{ xs: 2 }} mx="auto">
        {step <= 2 ? (
          <Typography
            color="textPrimary"
            variant="h5"
            textAlign="center"
            sx={{ marginBottom: '48px' }}
          >
            {step === 1
              ? translate('resources.goals.create.titles.step1')
              : translate('resources.goals.create.titles.step2')}
          </Typography>
        ) : null}
        <CustomForm step={step} setStep={setStep} record={record} data={data} />
      </Box>
    </Box>
  );
};
